import type { ComponentDocs } from '../component-docs.js';

/** A round picture with initials, standing in for a person's photo. */
const FACE = `data:image/svg+xml,${encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32"><circle cx="16" cy="16" r="16" fill="#4f5bd5"/>' +
    '<text x="16" y="21" font-family="sans-serif" font-size="13" font-weight="600" fill="white" text-anchor="middle">DR</text></svg>',
)}`;

/** A building mark, standing in for an icon that says what kind of thing the label names. */
const BUILDING = `data:image/svg+xml,${encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="#667085" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' +
    '<rect x="4" y="2" width="16" height="20" rx="2"/><path d="M9 22v-4h6v4M8 6h.01M12 6h.01M16 6h.01M8 10h.01M12 10h.01M16 10h.01"/></svg>',
)}`;

/**
 * What `<caos-tag>` says about itself.
 *
 * A tag is a free-form operational label somebody put on a record: Rush, Repeat customer, Site
 * visit needed. Any number of them, no vocabulary behind them, and no bearing on where the record
 * is in its life. That last part is why this is not `<caos-status-pill>` — see the note there, and
 * the `beside-a-tag` example on it. The visual language keeps the two apart without anyone having
 * to learn a convention: a tag is a SQUARE-cornered neutral chip on a filled surface, a pill is a
 * round-cornered tinted outline with no fill.
 *
 * The removal contract is the one thing to read before using `removable`. The dismiss control
 * raises `caos-tag-remove` and THE TAG DOES NOT REMOVE ITSELF — the host takes the tag off the
 * record and re-renders. That is the right way round for a label that is persisted somewhere: a
 * chip that vanishes before the write has been accepted is a chip that lies when the write fails.
 * It is also the opposite of `<caos-notice-banner>`, whose close control does remove the banner, so
 * the two are worth not assuming from each other. The `removable` example below declares the host's
 * half so the control does the real thing rather than nothing.
 *
 * The event is dispatched `cancelable: true`. The tag has no default action of its own to prevent, but
 * `requestRemove` returns the answer, and a pill container reads it: a refused removal leaves focus where
 * it was rather than waiting for a tag that is not going.
 *
 * CAOS-597 — AN AVATAR AND AN ERROR. Whatever is placed with `slot="avatar"` sits ahead of the label,
 * drawn round at the label's height: a person's picture on a pill that stands for them, or an icon
 * saying what kind of thing the label names. `error` marks the label as wrong — an address that
 * bounced, a value that no longer resolves — in the danger colour, with a warning mark in the avatar's
 * place and the words "Error:" read ahead of the label.
 */
const docs: ComponentDocs = {
  aliases: ['chip', 'label', 'keyword', 'token', 'pill', 'category', 'avatar pill'],

  answers: [
    'label a record with a word somebody typed',
    'mark an account as a repeat customer',
    'show the labels already on a record',
    'let somebody take a label off a record',
    'show which filters are currently applied',
    'show a person as a pill with their picture',
    'mark a label as wrong',
  ],

  placements: ['recordPage', 'homePage', 'appPage'],

  inputs: [
    {
      name: 'removable',
      attribute: 'removable',
      type: 'boolean',
      description:
        'Show the dismiss control. Pressing it reports `caos-tag-remove`; taking the tag off the record ' +
        'is the host\'s job, so set this only where something is listening.',
      default: false,
    },
    {
      name: 'error',
      attribute: 'error',
      type: 'boolean',
      description:
        'Mark the label as wrong — an address that bounced, a value that no longer resolves. The edge and ' +
        'lettering turn the danger colour, a warning mark takes the avatar\'s place, and "Error:" is read ahead of the label.',
      default: false,
    },
  ],

  events: [
    {
      name: 'caos-tag-remove',
      when:
        'The dismiss control is pressed. It bubbles and crosses the shadow boundary, so a list of tags ' +
        'can be listened to once at the container. The tag does NOT take itself off — it reports, and ' +
        'the host removes it once the record has been updated.',
      carries:
        'nothing — the tag that raised it is the event target, which is what a host listening at the ' +
        'container reads.',
    },
  ],

  state: [],

  slots: [
    { name: '', takes: 'The label. Set in 10px tracked capitals, and it does not wrap.' },
    {
      name: 'avatar',
      takes:
        "A picture or an icon for what the label is — a person's photo, a record's mark. Drawn round at the " +
        'label\'s height, ahead of it; nothing is drawn, and no gap kept, when nothing is placed. Hidden while in error.',
    },
  ],

  tokens: [
    { token: '--caos-color-surface-2', controls: 'The chip fill.' },
    { token: '--caos-color-border', controls: 'The 1px chip edge.' },
    { token: '--caos-color-text-muted', controls: 'The label lettering, and the dismiss glyph at rest.' },
    { token: '--caos-color-danger', controls: 'The dismiss glyph under the pointer, and the edge, lettering and mark of a tag in error.' },
    { token: '--caos-radius', controls: 'The chip corners — square, which is what tells a tag from a pill at a glance.' },
    { token: '--caos-radius-sm', controls: 'The corners of the dismiss control.' },
    { token: '--caos-focus-ring', controls: 'The ring on the dismiss control when it is reached by keyboard.' },
    { token: '--caos-font-mono', controls: 'The typeface the label is set in.' },
    { token: '--caos-space-2', controls: 'The gap between the label and the dismiss control.' },
  ],

  parts: [
    { part: 'tag', is: 'the tag' },
    { part: 'remove', is: 'the remove control' },
    { part: 'avatar', is: 'the place the avatar is drawn' },
    { part: 'error', is: 'the warning mark of a tag in error' },
  ],

  methods: [
    {
      name: 'requestRemove',
      description:
        'Report that somebody asked to take this tag off, exactly as the dismiss control does: raises caos-tag-remove ' +
        'and returns whether it went unrefused. The tag still does not remove itself. A pill container calls it for Delete.',
      args: [],
    },
  ],

  placing:
    'Inline, in a row after the thing being labelled — under a record header, at the end of a list row, ' +
    'beside a filter bar. It is `inline-flex` and vertically centred, so it sits on a line of text ' +
    'without disturbing it, and it never wraps internally: a long label makes a long chip. Several in ' +
    'a row need a wrapping container with a gap; the tag brings no spacing of its own.',

  whenToUse:
    'Labels a person applied, where there may be none, one or several on the same record, and where ' +
    'the words come from the people using the system rather than from the object model.',

  insteadUse: [
    {
      name: 'PillContainer',
      when:
        'Several tags belong together as one set — the values of a multi-select, the filters applied to a list — and ' +
        'the row wants one tab stop, arrow keys between the tags, and a limit past which the rest wait.',
    },
    {
      name: 'StatusPill',
      when:
        'The word is the record\'s LIFECYCLE STATE — Draft, In review, Approved — from the vocabulary ' +
        'the object defines, with exactly one true at any moment. That is the line: a pill is one ' +
        'value from a closed set and it changes as the record progresses; a tag is any number of ' +
        'values from an open set and it changes when somebody decides. Drawing a state as a tag throws ' +
        'away the tint that let a person read a list of fifty rows without stopping, and drawing a ' +
        'label as a pill means a colour now has two meanings and neither can be trusted.',
    },
    {
      name: 'PicklistField',
      when:
        'The person is CHOOSING the labels rather than reading them. Tags show what is on the record; ' +
        'a field is what puts it there.',
    },
    {
      name: 'SegmentedFilter',
      when:
        'The chips are controls that change what is listed rather than labels describing a record. A ' +
        'tag is not a control — the only thing pressable on it is the dismiss.',
    },
    {
      name: 'ProvenanceMarker',
      when:
        'The chip qualifies a single VALUE and says where it came from, rather than labelling the ' +
        'record as a whole.',
    },
  ],

  accessibility:
    'The label is ordinary text and reads as part of the line it sits in — no role is set, because a ' +
    'tag is content rather than a control. The dismiss IS a real button: reachable by keyboard, ' +
    'activated by Enter or Space, with its own focus ring and an accessible name — except inside a pill ' +
    'container, where it leaves the tab order because the tag itself takes focus and Delete removes it. ' +
    'Left to the author: ' +
    'the name on that button is "Remove" for every tag, so a screen-reader user working down a row of ' +
    'five hears "Remove" five times with nothing to tell them apart — until the component takes the ' +
    'label into that name, a row of removable tags needs its own labelling around it. A tag in error carries ' +
    'the words "Error:" ahead of its label, so the state is never carried by the colour and the mark alone.',

  examples: [
    {
      key: 'labels',
      label: 'Labels on a record',
      summary: 'The plain form: two operational labels somebody put on an account, reading as content.',
      placement: [
        { component: 'caos-tag', children: ['Rush'] },
        { component: 'caos-tag', children: ['Repeat customer'] },
        { component: 'caos-tag', children: ['Site visit needed'] },
      ],
    },
    {
      key: 'removable',
      label: 'Removable',
      summary:
        'The dismiss control, wired the way a real page wires it — the tag reports the press and the ' +
        'page takes it off, which is why it actually disappears here instead of doing nothing.',
      placement: [
        { component: 'caos-tag', children: ['Rush'] },
        { component: 'caos-tag', inputs: { removable: true }, children: ['Repeat customer'] },
        { component: 'caos-tag', inputs: { removable: true }, children: ['Site visit needed'] },
      ],
      host: {
        summary:
          'The surrounding page takes the tag off the record when it reports the press — the tag only ' +
          'reports it, and never removes itself.',
        run: (stage: HTMLElement): void => {
          for (const tag of Array.from(stage.querySelectorAll('caos-tag[removable]'))) {
            tag.addEventListener('caos-tag-remove', () => tag.remove());
          }
        },
      },
    },
    {
      key: 'with-avatar',
      label: 'With an avatar',
      summary: 'A tag standing for a person, their picture placed in the avatar slot ahead of their name.',
      placement: {
        component: 'caos-tag',
        children: [{ component: 'img', slot: 'avatar', inputs: { src: FACE, alt: '' } }, 'Dana Reyes'],
      },
    },
    {
      key: 'with-icon',
      label: 'With an icon',
      summary: 'The same slot holding an icon instead, saying the label names an account rather than a person.',
      placement: {
        component: 'caos-tag',
        children: [{ component: 'img', slot: 'avatar', inputs: { src: BUILDING, alt: '' } }, 'Meridian Supply'],
      },
    },
    {
      key: 'with-error',
      label: 'In error',
      summary: 'A recipient whose address bounced, beside one that did not: red, marked, and saying so in words.',
      placement: [
        { component: 'caos-tag', inputs: { removable: true }, children: ['dana@meridian.example'] },
        { component: 'caos-tag', inputs: { removable: true, error: true }, children: ['orders@meridan.example'] },
      ],
    },
  ],
};

export default docs;
